import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import StatusBadge from "@/components/shared/StatusBadge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import { Plus, Search, MapPin, Phone, Calendar, RefreshCw, ChevronRight, Hash, MoreVertical, Eye, Pencil, Trash2, Loader2, Filter, X } from "lucide-react";
import VehicleProfileDialog from "@/components/shared/VehicleProfileDialog";
import OnboardVehicleModal from "@/components/shared/OnboardVehicleModal";
import { toast } from "sonner";
import { useParams } from "react-router-dom";

export default function VehiclesPage() {
  const { vehicleId: deepLinkedVehicleId } = useParams();
  const { can } = useAuth();
  const canCreateVehicles = can("VEHICLES.CREATE");
  const canUpdateVehicles = can("VEHICLES.UPDATE");
  const canDeleteVehicles = can("VEHICLES.DELETE");
  const canEditVin = can("VEHICLES.EDIT_VIN");
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalVehicles, setTotalVehicles] = useState(0);

  // Edit API-driven vehicle panel
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editVehicleId, setEditVehicleId] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);
  const [editForm, setEditForm] = useState({
    registration_number: "",
    vin: "",
    make: "",
    model: "",
    year: "",
    vehicle_type: "",
    is_active: true,
    metadata: "",
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Filters State
  const [q, setQ] = useState("");
  const [vehicleCategoryFilter, setVehicleCategoryFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [isActiveFilter, setIsActiveFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    states: [],
    districts: [],
    cities: [],
  });

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resetFilters = () => {
    setQ("");
    setVehicleCategoryFilter("all");
    setStateFilter("all");
    setDistrictFilter("all");
    setCityFilter("all");
    setIsActiveFilter("all");
    setPage(1);
  };

  const activeFiltersCount = useMemo(() => {
    return [
      vehicleCategoryFilter !== "all",
      stateFilter !== "all",
      districtFilter !== "all",
      cityFilter !== "all",
      isActiveFilter !== "all",
    ].filter(Boolean).length;
  }, [vehicleCategoryFilter, stateFilter, districtFilter, cityFilter, isActiveFilter]);

  const buildFilterOptions = useCallback((items = []) => {
    const categories = new Set();
    const states = new Set();
    const districts = new Set();
    const cities = new Set();

    items.forEach((vehicle) => {
      if (vehicle?.vehicle_category) categories.add(vehicle.vehicle_category);
      if (vehicle?.state) states.add(vehicle.state);
      if (vehicle?.district) districts.add(vehicle.district);
      if (vehicle?.city) cities.add(vehicle.city);
    });

    return {
      categories: [...categories].sort((a, b) => a.localeCompare(b)),
      states: [...states].sort((a, b) => a.localeCompare(b)),
      districts: [...districts].sort((a, b) => a.localeCompare(b)),
      cities: [...cities].sort((a, b) => a.localeCompare(b)),
    };
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      let currentPage = 1;
      let totalPagesForOptions = 1;
      const collectedVehicles = [];

      while (currentPage <= totalPagesForOptions) {
        const { data } = await api.get("/vehicles", {
          params: {
            page: currentPage,
            page_size: 200,
          },
        });

        const pageItems = Array.isArray(data?.data) ? data.data : [];
        const total = Number(data?.meta?.total) || pageItems.length;
        totalPagesForOptions = Math.max(1, Math.ceil(total / 200));
        collectedVehicles.push(...pageItems);

        if (!pageItems.length) break;
        currentPage += 1;
      }

      setFilterOptions(buildFilterOptions(collectedVehicles));
    } catch {
      setFilterOptions((current) => current);
    }
  }, [buildFilterOptions]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {};

      params.page = page;
      params.page_size = pageSize;
      if (q.trim()) params.search = q.trim();
      if (vehicleCategoryFilter !== "all") params.vehicle_category = vehicleCategoryFilter;
      if (isActiveFilter !== "all") params.is_active = isActiveFilter === "true";
      if (stateFilter !== "all") params.state = stateFilter;
      if (districtFilter !== "all") params.district = districtFilter;
      if (cityFilter !== "all") params.city = cityFilter;

      const { data } = await api.get("/vehicles", { params });
      setVehicles(Array.isArray(data?.data) ? data.data : []);
      setTotalVehicles(Number(data?.meta?.total) || 0);
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setVehicles([]);
      setTotalVehicles(0);
    } finally {
      setLoading(false);
    }
  }, [q, vehicleCategoryFilter, isActiveFilter, stateFilter, districtFilter, cityFilter, page, pageSize]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (!deepLinkedVehicleId) return;
    setSelectedVehicleId(deepLinkedVehicleId);
  }, [deepLinkedVehicleId]);

  const handleEditVehicle = async (vehicleId) => {
    setEditOpen(true);
    setEditVehicleId(vehicleId);
    setEditLoading(true);
    setEditError("");
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}`);
      setEditOriginal(data);
      setEditForm({
        registration_number: data?.registration_number || "",
        vin: data?.vin || "",
        make: data?.make || "",
        model: data?.model || "",
        year: data?.year ? String(data.year) : "",
        vehicle_type: data?.vehicle_type || "",
        is_active: data?.is_active ?? true,
        metadata: data?.metadata ? JSON.stringify(data.metadata, null, 2) : "",
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to load vehicle for editing.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateVehicle = async () => {
    if (!editVehicleId || !editOriginal) return;

    const payload = {};
    const normalizeText = (v) => (v || "").trim();

    if (normalizeText(editForm.registration_number) !== normalizeText(editOriginal.registration_number)) {
      payload.registration_number = normalizeText(editForm.registration_number) || undefined;
    }
    if (normalizeText(editForm.vin) !== normalizeText(editOriginal.vin)) {
      payload.vin = normalizeText(editForm.vin) || undefined;
    }
    if (normalizeText(editForm.make) !== normalizeText(editOriginal.make)) {
      payload.make = normalizeText(editForm.make) || undefined;
    }
    if (normalizeText(editForm.model) !== normalizeText(editOriginal.model)) {
      payload.model = normalizeText(editForm.model) || undefined;
    }
    if (normalizeText(editForm.vehicle_type) !== normalizeText(editOriginal.vehicle_type)) {
      payload.vehicle_type = normalizeText(editForm.vehicle_type) || undefined;
    }

    const originalYear = editOriginal.year ? String(editOriginal.year) : "";
    if ((editForm.year || "").trim() !== originalYear) {
      const parsedYear = parseInt(editForm.year, 10);
      if (editForm.year.trim() && Number.isNaN(parsedYear)) {
        setEditError("Year must be a valid number.");
        return;
      }
      payload.year = editForm.year.trim() ? parsedYear : undefined;
    }

    if ((editForm.is_active ?? true) !== (editOriginal.is_active ?? true)) {
      payload.is_active = editForm.is_active;
    }

    const metadataText = (editForm.metadata || "").trim();
    const originalMetadataText = editOriginal.metadata ? JSON.stringify(editOriginal.metadata) : "";
    if (metadataText !== originalMetadataText && metadataText !== JSON.stringify(editOriginal.metadata, null, 2)) {
      if (!metadataText) {
        payload.metadata = {};
      } else {
        try {
          payload.metadata = JSON.parse(metadataText);
        } catch {
          setEditError("Metadata must be valid JSON.");
          return;
        }
      }
    }

    if (!Object.keys(payload).length) {
      toast.message("No changes to update.");
      return;
    }

    setEditSubmitting(true);
    setEditError("");
    try {
      await api.patch(`/vehicles/${editVehicleId}`, payload);
      toast.success("Vehicle updated successfully.");
      setEditOpen(false);
      await fetchVehicles();
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to update vehicle.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteVehicle = async () => {
    if (!deleteTarget) return;

    setDeleteSubmitting(true);
    try {
      await api.delete(`/vehicles/${deleteTarget.id}`);
      toast.success("Vehicle deleted successfully.");
      setDeleteOpen(false);
      setDeleteTarget(null);
      if (selectedVehicleId === deleteTarget.id) {
        setSelectedVehicleId(null);
      }
      setPage((currentPage) => {
        if (currentPage > 1 && vehicles.length === 1) {
          return currentPage - 1;
        }
        return currentPage;
      });
      await fetchVehicles();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to delete vehicle.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalVehicles / pageSize));

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <PageHeader
        title="Vehicles"
        subtitle="Manage and monitor all connected vehicles in the fleet."
        actions={
          canCreateVehicles ? (
            <Button onClick={() => setIsAddingVehicle(true)} className="bg-slate-900 text-white hover:bg-slate-800">
              <Plus size={14} className="mr-1.5" /> Onboard vehicle
            </Button>
          ) : null
        }
      />

      {/* Advanced Filter Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 space-y-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Vehicle filters</h3>
          <p className="text-[11px] text-slate-500">Search by vehicle, address, pincode, city and document status.</p>
        </div>

        <Accordion
          type="single"
          collapsible
          value={filtersOpen ? "vehicle-filters" : undefined}
          onValueChange={(value) => setFiltersOpen(value === "vehicle-filters")}
          className="space-y-4"
        >
          <AccordionItem value="vehicle-filters" className="border-none">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative flex-1 min-w-[240px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by registration, make, model"
                  className="pl-9 h-11 border-slate-200 bg-slate-50/50"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:flex-none">
                <AccordionTrigger className="h-11 rounded-xl border border-slate-200 bg-white px-4 py-0 text-slate-700 hover:bg-slate-50 hover:no-underline">
                  <span className="flex items-center gap-2">
                    <Filter size={14} className="text-slate-400" />
                    <span>Filters</span>
                    {activeFiltersCount > 0 && (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-900 px-1.5 text-[10px] font-bold text-white">
                        {activeFiltersCount}
                      </span>
                    )}
                  </span>
                </AccordionTrigger>

                <Button
                  variant="outline"
                  onClick={fetchVehicles}
                  className="h-11 gap-2 border-slate-200 bg-white hover:bg-slate-50"
                  title="Refresh vehicles"
                >
                  <RefreshCw size={14} />
                  Refresh
                </Button>
              </div>
            </div>

            <AccordionContent className="pt-4">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    <Select value={vehicleCategoryFilter} onValueChange={(value) => {
                      setVehicleCategoryFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="h-11 w-full border-slate-200 bg-white">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {filterOptions.categories.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Select value={stateFilter} onValueChange={(value) => {
                      setStateFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="h-11 w-full border-slate-200 bg-white">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {filterOptions.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Select value={districtFilter} onValueChange={(value) => {
                      setDistrictFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="h-11 w-full border-slate-200 bg-white">
                        <SelectValue placeholder="All Districts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Districts</SelectItem>
                        {filterOptions.districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Select value={cityFilter} onValueChange={(value) => {
                      setCityFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="h-11 w-full border-slate-200 bg-white">
                        <SelectValue placeholder="All Cities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Cities</SelectItem>
                        {filterOptions.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Select value={isActiveFilter} onValueChange={(value) => {
                      setIsActiveFilter(value);
                      setPage(1);
                    }}>
                      <SelectTrigger className="h-11 w-full border-slate-200 bg-white">
                        <SelectValue placeholder="Active status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <div className="font-medium">
                    Showing <span className="font-bold text-slate-900">{vehicles.length}</span> of <span className="font-bold text-slate-900">{totalVehicles}</span> vehicles
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {activeFiltersCount > 0 && (
                      <Button variant="ghost" className="h-9 px-2 text-slate-500 hover:bg-white hover:text-slate-900" onClick={resetFilters}>
                        <X size={14} className="mr-1.5" /> Clear filters
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Page size</span>
                      <Select value={String(pageSize)} onValueChange={(value) => {
                        setPageSize(Number(value));
                        setPage(1);
                      }}>
                        <SelectTrigger className="h-9 w-[88px] border-slate-200 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12</SelectItem>
                          <SelectItem value="24">24</SelectItem>
                          <SelectItem value="48">48</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Page {page} of {totalPages}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {vehicles.map((v) => (
          <div
            key={v.id}
            onClick={() => setSelectedVehicleId(v.id)}
            className="group cursor-pointer rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden transition-all hover:shadow-xl hover:border-blue-300 relative flex flex-col h-full"
          >
            {/* High-Impact Background Car Image */}
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?q=80&w=1000&auto=format&fit=crop" 
                alt="" 
                className="w-full h-full object-cover object-[center_65%] opacity-[0.08] group-hover:opacity-[0.75] transition-all duration-700 scale-110 group-hover:scale-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/90"></div>
            </div>

            <div className="p-6 space-y-6 relative z-10 flex-1">
              {/* Card Top */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{v.vehicle_category || "VEHICLE"}</p>
                  <h3 className="text-[28px] font-black text-slate-900 leading-none mt-1 group-hover:text-blue-600 transition-colors">{v.registration_number}</h3>
                  <p className="text-[13px] font-bold text-slate-500 mt-1.5">{v.make} {v.model}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={v.status} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedVehicleId(v.id);
                        }}
                      >
                        <Eye size={14} /> View
                      </DropdownMenuItem>
                      {canUpdateVehicles ? (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditVehicle(v.id);
                          }}
                        >
                          <Pencil size={14} /> Edit
                        </DropdownMenuItem>
                      ) : null}
                      {canDeleteVehicles ? (
                        <DropdownMenuItem
                          className="text-rose-600 focus:text-rose-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: v.id, name: v.registration_number || v.make || "this vehicle" });
                            setDeleteOpen(true);
                          }}
                        >
                          <Trash2 size={14} /> Delete
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Card Grid Info */}
              <div className="grid grid-cols-2 gap-3">
                <InfoItem label="MAKE" value={v.make} />
                <InfoItem label="MODEL" value={v.model} />
                <InfoItem label="YEAR" value={String(v.year)} />
                <InfoItem label="TYPE" value={v.vehicle_type} />
              </div>

              {/* Location & Details Block */}
              <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100 space-y-3.5 group-hover:bg-white transition-colors">
                <div className="flex gap-3">
                  <MapPin size={16} className="text-slate-400 shrink-0 mt-0.5 group-hover:text-blue-500 transition-colors" />
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Location</p>
                    <p className="text-[13px] font-bold text-slate-900">{v.city || "N/A"}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Phone size={16} className="text-slate-400 shrink-0 mt-0.5 group-hover:text-blue-500 transition-colors" />
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Driver</p>
                    <p className="text-[13px] font-bold text-slate-900">{v.driver?.name || "N/A"}</p>
                  </div>
                </div>

                {/* Incident Analytics Box */}
                <div className="pt-1">
                  <div className="grid grid-cols-2 gap-2 bg-white/50 p-1.5 rounded-2xl border border-slate-100">
                    <button className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-rose-50/50 border border-rose-100/50 hover:bg-rose-50 transition-all group/inc">
                      <div className="text-left">
                        <p className="text-[9px] font-black text-rose-400 uppercase tracking-tighter leading-none mb-1">Incidents</p>
                        <p className="text-[11px] font-bold text-rose-600">Count</p>
                      </div>
                      <span className="w-6 h-6 rounded-lg bg-rose-600 text-white text-[11px] font-black flex items-center justify-center shadow-sm">
                        {v.incident_count || 0}
                      </span>
                    </button>
                    <button className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-50/50 border border-slate-100/50 hover:bg-slate-50 transition-all group/inc">
                      <div className="text-left">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter leading-none mb-1">Device</p>
                        <p className="text-[11px] font-bold text-slate-600">Status</p>
                      </div>
                      <span className={`w-6 h-6 rounded-lg text-white text-[11px] font-black flex items-center justify-center shadow-sm ${v.status === 'offline' ? 'bg-slate-400' : 'bg-emerald-500'}`}>
                        {v.status === 'offline' ? '✕' : '✓'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Last Trip */}
              <div className="flex items-center gap-2.5 text-slate-500">
                <Calendar size={14} className="group-hover:text-blue-500 transition-colors" />
                <div className="text-[10px] uppercase font-black tracking-widest">
                  <p className="text-slate-300">LAST TRIP</p>
                  <p className="text-slate-900">{v.last_trip ? new Date(v.last_trip).toLocaleDateString() : "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Card Footer */}
            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between relative z-10 group-hover:bg-blue-50/30 transition-colors">
              <div className="flex items-center gap-2 text-slate-500">
                <Hash size={14} />
                <span className="text-xs font-bold uppercase tracking-tight">Profile</span>
              </div>
              <div 
                className="text-sm font-black text-blue-600 group-hover:text-blue-700 flex items-center gap-1 group/btn"
              >
                Open <ChevronRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && vehicles.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <Search size={48} className="mx-auto text-slate-200 mb-4" />
          <p className="text-slate-900 font-bold">No vehicles found</p>
          <p className="text-slate-500 text-sm mt-1">Try adjusting your filters or search terms</p>
        </div>
      )}

      {!loading && totalVehicles > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            variant="outline"
            className="border-slate-200"
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            className="border-slate-200"
            onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4">
            <RefreshCw size={24} className="text-slate-600 animate-spin" />
          </div>
          <p className="text-slate-900 font-bold">Loading vehicles...</p>
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={(open) => !open && setEditOpen(false)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit Vehicle</DialogTitle>
          </DialogHeader>

          {editLoading ? (
            <div className="py-10 flex items-center justify-center text-slate-500">
              <Loader2 size={22} className="animate-spin mr-2" /> Loading vehicle...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Registration Number</Label>
                  <Input value={editForm.registration_number} onChange={(e) => handleEditFormChange("registration_number", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>VIN</Label>
                  <Input
                    value={editForm.vin}
                    onChange={(e) => handleEditFormChange("vin", e.target.value)}
                    readOnly={!canEditVin}
                    aria-readonly={!canEditVin}
                    className={!canEditVin ? "bg-slate-100 cursor-not-allowed" : undefined}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Vehicle Type</Label>
                  <Input value={editForm.vehicle_type} onChange={(e) => handleEditFormChange("vehicle_type", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Make</Label>
                  <Input value={editForm.make} onChange={(e) => handleEditFormChange("make", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Model</Label>
                  <Input value={editForm.model} onChange={(e) => handleEditFormChange("model", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Year</Label>
                  <Input value={editForm.year} onChange={(e) => handleEditFormChange("year", e.target.value)} placeholder="e.g. 2024" />
                </div>
                <div className="space-y-1.5">
                  <Label>Active Status</Label>
                  <Select value={editForm.is_active ? "true" : "false"} onValueChange={(v) => handleEditFormChange("is_active", v === "true")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Retired / Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>



              {editError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {editError}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
                <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleUpdateVehicle} disabled={editSubmitting}>
                  {editSubmitting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving...</> : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !deleteSubmitting) {
            setDeleteOpen(false);
            setDeleteTarget(null);
          }
        }}
        title="Delete vehicle?"
        description={
          <>
            Are you sure you want to delete <span className="font-bold text-slate-900">{deleteTarget?.name}</span>?
            {" "}This will permanently remove the vehicle via the admin delete endpoint.
          </>
        }
        confirmLabel="Yes, delete"
        variant="danger"
        loading={deleteSubmitting}
        onConfirm={handleDeleteVehicle}
      />

      <VehicleProfileDialog 
        open={!!selectedVehicleId} 
        vehicleId={selectedVehicleId} 
        onOpenChange={(val) => !val && setSelectedVehicleId(null)} 
      />
      <OnboardVehicleModal 
        isOpen={isAddingVehicle} 
        onClose={() => setIsAddingVehicle(false)} 
        onSuccess={() => {
          setIsAddingVehicle(false);
          fetchVehicles();
        }}
      />
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-100/50">
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{label}</p>
      <p className="text-[13px] font-bold text-slate-900 truncate">{value}</p>
    </div>
  );
}
